import { AnyAction } from '@reduxjs/toolkit'

import { PageId } from 'routing/pages'
import { CobaltPath } from 'routing/Path'
import {
    ActionForPagePayload,
    ActionForPathPayload,
    ActionForResourcePayload,
} from 'types'

import { getCurrentPage } from '../routing/pagePaths'

type PayloadActionCreator<Payload> = (payload: Payload) => AnyAction

export interface PathActionEmptyPayload {
    path: CobaltPath
    options?: unknown
}

export interface PathActionPayload<T> extends PathActionEmptyPayload {
    data: T
}

export const curryEmptyPayloadActionForPath =
    (actionCreator: PayloadActionCreator<PathActionEmptyPayload>) =>
    (path: CobaltPath, options: unknown = {}) =>
    (): AnyAction =>
        actionCreator({
            path,
            options,
        })

/**
 * Currys action creator to inject path in payload
 */
export const curryPayloadActionForPath =
    <T>(actionCreator: PayloadActionCreator<PathActionPayload<T>>) =>
    (path: CobaltPath, options: unknown = {}) =>
    (data: T): AnyAction =>
        actionCreator({
            path,
            options,
            data,
        })

/**
 * Currys action creator to inject path in payload
 */
export const curryActionForPath =
    <T = unknown>(
        actionCreator: PayloadActionCreator<ActionForPathPayload<T>>
    ) =>
    (path?: CobaltPath | PageId, options: unknown = {}) =>
    (data?: T): AnyAction =>
        actionCreator({
            path,
            options,
            data,
        } as ActionForPathPayload<T>)

/**
 * Currys action creator to inject pageName in payload
 * If pageName is not provided, assumes current page
 */
export const curryActionForPage =
    <T = unknown>(
        actionCreator: PayloadActionCreator<ActionForPagePayload<T>>
    ) =>
    (pageName?: PageId) =>
    (data?: T): AnyAction =>
        actionCreator({
            pageName: pageName || getCurrentPage().page,
            data,
        } as ActionForPagePayload<T>)

/**
 * Currys action creator to inject pageName and updatingResource state name in payload
 */
export const curryActionForResource =
    <T = unknown>(
        actionCreator: PayloadActionCreator<ActionForResourcePayload<T>>
    ) =>
    (pageName?: PageId | CobaltPath, updatingResourceKey = 'updating') =>
    (data?: T): AnyAction =>
        actionCreator({
            pageName,
            updatingResourceKey,
            data,
        } as ActionForResourcePayload<T>)
